// dNews custom alert system - replaces SweetAlert2 inside the dNews module.
//
// Two surfaces:
//   1. Toast stack (top-right corner) - non-blocking feedback for
//      success/error/warning/info. Auto-dismisses after a timer.
//   2. Centred modal - blocking confirm or text-prompt with a backdrop.
//
// Imperative API mirrors the Swal patterns we replaced, so call sites stay
// short:
//
//   dnAlert.success("Saved");
//   dnAlert.error("Send failed", e.message);
//   const ok = await dnAlert.confirm({ title: "Delete?", message: "...", danger: true });
//   const value = await dnAlert.prompt({ title: "New group", placeholder: "Group name" });
//   await dnAlert.report({ title: "Blocked", html: table, wide: true });
//
// A toast is a single line of text. When a failure has per-row detail (which
// employee, which sheet row, why), it needs report() - a toast cannot hold a
// table, and collapsing one into "3 rows failed" throws away the only part
// the user can act on.
//
// Mount <DNewsAlertHost /> ONCE somewhere near the app root so the toasts
// + modal have a place to render.

import React, { useEffect, useState } from "react";
import {
    FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaInfoCircle, FaTimes,
} from "react-icons/fa";
import "./dNews_Alert.css";

// ── Singleton state + pub/sub ────────────────────────────────────────────────
// Plain JS so callers can fire alerts from anywhere (event handlers, async
// callbacks, even module-load time) without needing a React context.
const state = {
    toasts: [],   // [{ id, kind, title, text }]
    modal:  null, // { kind: "confirm"|"prompt", ... resolve }
};
const subscribers = new Set();
function publish() { subscribers.forEach(fn => fn()); }
let nextId = 1;

// ── Toast helpers ────────────────────────────────────────────────────────────
// Each toast tracks its own dismiss timer + remaining ms so hovering pauses
// the countdown (users need time to read long error lists) and the timer
// resumes on mouse-leave with only the leftover time.
const toastTimers = new Map(); // id → { timerId, remainingMs, resumeAt }

function pushToast(kind, title, text, timer) {
    const id = nextId++;
    state.toasts = [...state.toasts, { id, kind, title, text }];
    publish();
    if (timer > 0) {
        const timerId = setTimeout(() => dismissToast(id), timer);
        toastTimers.set(id, { timerId, remainingMs: timer, resumeAt: Date.now() });
    }
    return id;
}
function pauseToast(id) {
    const entry = toastTimers.get(id);
    if (!entry) return;
    clearTimeout(entry.timerId);
    const elapsed = Date.now() - entry.resumeAt;
    entry.remainingMs = Math.max(500, entry.remainingMs - elapsed); // keep at least 0.5s
    entry.timerId = null;
    toastTimers.set(id, entry);
}
function resumeToast(id) {
    const entry = toastTimers.get(id);
    if (!entry || entry.timerId) return; // already running
    entry.resumeAt = Date.now();
    entry.timerId = setTimeout(() => dismissToast(id), entry.remainingMs);
    toastTimers.set(id, entry);
}
function dismissToast(id) {
    const entry = toastTimers.get(id);
    if (entry && entry.timerId) clearTimeout(entry.timerId);
    toastTimers.delete(id);
    state.toasts = state.toasts.filter(t => t.id !== id);
    publish();
}

// ── Modal helpers ────────────────────────────────────────────────────────────
function openModal(opts) {
    return new Promise(resolve => {
        // If a modal is already open, queueing gets complicated. In practice
        // we don't open two at once - if we ever do, resolve the previous
        // one as cancelled so we don't strand its caller.
        if (state.modal) {
            try { state.modal.resolve(state.modal.kind === "prompt" ? null : false); } catch {}
        }
        state.modal = { ...opts, resolve };
        publish();
    });
}
function closeModal(value) {
    if (!state.modal) return;
    const m = state.modal;
    state.modal = null;
    publish();
    try { m.resolve(value); } catch {}
}

// ── Public API ───────────────────────────────────────────────────────────────
export const dnAlert = {
    success: (title, text) => pushToast("success", title, text, 2500),
    error:   (title, text) => pushToast("error",   title, text, 4500),
    warning: (title, text) => pushToast("warning", title, text, 3500),
    info:    (title, text) => pushToast("info",    title, text, 2500),

    // confirm({ title, message?, html?, confirmLabel?, cancelLabel?, danger? })
    //   message: plain text (escaped)
    //   html:    raw HTML (rendered with dangerouslySetInnerHTML) - prefer
    //            message unless you really need formatted bullets / lines.
    //   danger:  flips the confirm button red for delete-like actions.
    // returns: Promise<boolean>
    confirm: (opts) => openModal({ kind: "confirm", ...opts }),

    // prompt({ title, placeholder?, defaultValue?, confirmLabel?, cancelLabel?, validate? })
    //   validate(value) → string error message or null/undefined to accept
    // returns: Promise<string|null>  (null when cancelled)
    prompt: (opts) => openModal({ kind: "prompt", ...opts }),

    // report({ title, html, wide?, danger?, cancelLabel? })
    //   A blocking, Close-only modal for failures that carry per-row detail
    //   (error tables, blocked-employee lists). Use when a toast would have to
    //   throw the detail away. Pair `wide` with a table.
    // returns: Promise<false>  (resolves when dismissed)
    report: (opts) => openModal({ kind: "confirm", hideConfirm: true, cancelLabel: "Close", ...opts }),
};

// Escape untrusted values before interpolating them into a `html` payload.
// Employee names, emails and DB error strings all reach these modals, and the
// body is rendered with dangerouslySetInnerHTML.
export function escapeHtml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// ── Host component - mount once near the root ────────────────────────────────
const TOAST_ICONS = {
    success: FaCheckCircle,
    error:   FaTimesCircle,
    warning: FaExclamationTriangle,
    info:    FaInfoCircle,
};

export function DNewsAlertHost() {
    // Force re-render on every publish. Keeping the actual data in module
    // state (not React state) lets imperative calls fire from anywhere.
    const [, force] = useState(0);
    useEffect(() => {
        const fn = () => force(c => c + 1);
        subscribers.add(fn);
        return () => { subscribers.delete(fn); };
    }, []);

    return (
        <>
            {/* Toast stack */}
            <div className="dn-toast-stack" aria-live="polite">
                {state.toasts.map(t => {
                    const Icon = TOAST_ICONS[t.kind] || FaInfoCircle;
                    return (
                        <div
                            key={t.id}
                            className={`dn-toast dn-toast-${t.kind}`}
                            onMouseEnter={() => pauseToast(t.id)}
                            onMouseLeave={() => resumeToast(t.id)}
                        >
                            <Icon className="dn-toast-icon" />
                            <div className="dn-toast-body">
                                {t.title && <div className="dn-toast-title">{t.title}</div>}
                                {t.text && <div className="dn-toast-text">{t.text}</div>}
                            </div>
                            <button className="dn-toast-close" onClick={() => dismissToast(t.id)}>
                                <FaTimes />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Modal (confirm / prompt) */}
            {state.modal && <ModalCard modal={state.modal} onClose={closeModal} />}
        </>
    );
}

// ── Modal card ───────────────────────────────────────────────────────────────
function ModalCard({ modal, onClose }) {
    const [inputValue, setInputValue] = useState(modal.defaultValue || "");
    const [inputError, setInputError] = useState("");

    // Keyboard shortcuts:
    //   Esc   → cancel (returns null for prompt, false for confirm)
    //   Enter → confirm. For prompt this submits the typed value (with
    //           validation); for confirm it fires the primary action so a
    //           Delete dialog opened via the trash icon can be confirmed
    //           without grabbing the mouse.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                onClose(modal.kind === "prompt" ? null : false);
                return;
            }
            if (e.key === "Enter") {
                // Don't hijack Enter inside a textarea or button - those have
                // their own meaning. We're listening on window so this fires
                // even when the (auto-focused) prompt input has focus, which
                // is exactly the behaviour we want.
                if (modal.kind === "prompt")  tryConfirmPrompt();
                else if (modal.hideConfirm)   onClose(false); // report(): Enter just dismisses
                else                          onClose(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, modal.kind, modal.hideConfirm]);

    const tryConfirmPrompt = () => {
        if (modal.kind !== "prompt") return;
        if (typeof modal.validate === "function") {
            const err = modal.validate(inputValue);
            if (err) { setInputError(String(err)); return; }
        }
        onClose(inputValue);
    };

    return (
        // Backdrop has no onClick - kept losing typed input. Use Esc / Cancel.
        <div className="dn-modal-backdrop">
            <div className={`dn-modal ${modal.danger ? "danger" : ""} ${modal.wide ? "wide" : ""}`}>
                <div className="dn-modal-header">
                    <h3>{modal.title || (modal.kind === "prompt" ? "Input required" : "Confirm")}</h3>
                </div>

                <div className="dn-modal-body">
                    {modal.html ? (
                        // Only used by trusted internal callers (e.g. our own
                        // confirmation summaries). Never pass user-supplied
                        // HTML here.
                        <div dangerouslySetInnerHTML={{ __html: modal.html }} />
                    ) : modal.message ? (
                        <p>{modal.message}</p>
                    ) : null}

                    {modal.kind === "prompt" && (
                        <>
                            <input
                                type="text"
                                className="dn-modal-input"
                                value={inputValue}
                                onChange={e => { setInputValue(e.target.value); setInputError(""); }}
                                placeholder={modal.placeholder || ""}
                                autoFocus
                                maxLength={250}
                            />
                            {inputError && <div className="dn-modal-error">{inputError}</div>}
                        </>
                    )}
                </div>

                <div className="dn-modal-footer">
                    <button
                        type="button"
                        className="dn-modal-cancel"
                        onClick={() => onClose(modal.kind === "prompt" ? null : false)}
                    >
                        {modal.cancelLabel || "Cancel"}
                    </button>
                    {/* report() modals are Close-only - there is nothing to confirm. */}
                    {!modal.hideConfirm && (
                        <button
                            type="button"
                            className={`dn-modal-confirm ${modal.danger ? "danger" : ""}`}
                            onClick={() => {
                                if (modal.kind === "prompt") tryConfirmPrompt();
                                else                          onClose(true);
                            }}
                            // Auto-focus on confirm modals so the button has a
                            // visible focus ring + Enter triggers it. Prompt
                            // modals already auto-focus their input.
                            autoFocus={modal.kind !== "prompt"}
                        >
                            {modal.confirmLabel || (modal.kind === "prompt" ? "OK" : "Confirm")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
